import { Flex, Stack, Text } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { useEffect, useRef, useState } from 'react';

import {
  Maybe,
  TemporaryData,
  useDecryptTempInfoSsnLazyQuery,
} from '../../../gql/generated/graphql';
import { LabelWithInfo, ShowHideIcon } from './BaseSsnComponents';
import SSNInput from './SSNInput';

type Props = {
  tempInfoData?: Maybe<TemporaryData>;
  tempInfoId?: string;
};

const LoanAppSsnInput = ({ tempInfoData, tempInfoId }: Props) => {
  const isMethodFiFlow = tempInfoData?.is_methodfi_flow;
  const mustEnterSsn = !tempInfoData?.has_full_ssn;

  const [showInput, setShowInput] = useState(mustEnterSsn);
  const hasFetchedSsn = useRef(false);

  const container = { ml: { base: 0, md: 2 }, mb: { base: 4, md: 0 } };
  const { setFieldValue, values } = useFormikContext<{ ssn?: string }>();

  const [decryptTempInfoSSN] = useDecryptTempInfoSsnLazyQuery({
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (isMethodFiFlow && tempInfoId && !hasFetchedSsn.current) {
      hasFetchedSsn.current = true;
      decryptTempInfoSSN({ variables: { tempInfoId } })
        .then((result) => {
          const ssn = result.data?.decryptTempInfoSSN as string | null | undefined;
          if (ssn) {
            setFieldValue('ssn', ssn);
          } else {
            setFieldValue('must_enter_ssn', true);
            setShowInput(true);
          }
        })
        .catch(() => {
          setFieldValue('must_enter_ssn', true);
          setShowInput(true);
        });
    }
  }, [isMethodFiFlow, tempInfoId, decryptTempInfoSSN, setFieldValue]);

  useEffect(() => {
    if (isMethodFiFlow) {
      setShowInput(false);
    } else {
      setFieldValue('must_enter_ssn', mustEnterSsn);
      setShowInput(mustEnterSsn);
    }
  }, [mustEnterSsn, isMethodFiFlow, setFieldValue]);

  const onToggleInput = () => {
    setShowInput(!showInput);
    setFieldValue('must_enter_ssn', !showInput);
  };

  if (showInput) {
    return (
      <Stack w="100%" {...container}>
        <SSNInput digits="9" name="ssn" hideToggle={!!isMethodFiFlow} />
        {isMethodFiFlow && (
          <Text fontSize="12px" color="grayMedium" mt={1}>
            SSN auto-filled. Please verify the SSN is correct.
          </Text>
        )}
      </Stack>
    );
  }

  if (isMethodFiFlow) {
    const last4Digits = values.ssn?.slice(-4);

    return (
      <Stack w="100%" {...container}>
        <LabelWithInfo defaultLabel="SOCIAL SECURITY NUMBER" />
        <Flex
          mt={-0.5}
          borderWidth="1px"
          borderColor="grayDarkBackground"
          borderRadius="3px"
          h="36px"
          justify="space-between"
          align="center"
          px="10px"
        >
          <Text fontSize={16} textColor="charcoal" fontWeight={600}>
            •••-••-{last4Digits}
          </Text>
          <ShowHideIcon show={false} onClick={onToggleInput} />
        </Flex>
        <Text fontSize="12px" color="grayMedium" mt={1}>
          SSN auto-filled. Click the eye icon to verify or edit.
        </Text>
      </Stack>
    );
  }

  return (
    <Stack w="100%" {...container}>
      <LabelWithInfo defaultLabel="SOCIAL SECURITY NUMBER" />
      <Flex
        mt={-0.5}
        borderWidth="1px"
        borderColor="grayDarkBackground"
        borderRadius="3px"
        h="36px"
        justify="space-between"
        align="center"
        px="10px"
      >
        <Text fontSize={16} textColor="charcoal">
          •••••••••
        </Text>
        <ShowHideIcon show={false} onClick={onToggleInput} />
      </Flex>
    </Stack>
  );
};

export default LoanAppSsnInput;
